import React, { useState, useEffect } from 'react';
import { 
  Users, Shield, Search, MoreVertical, 
  UserPlus, Edit, Trash2, Check, AlertTriangle, Globe, X, CheckCircle2
} from 'lucide-react';
import { supabase } from '../config/supabase';
import { PermissionLevel } from '../types';
import { 
  PERMISSION_LABELS, 
  PERMISSION_COLORS, 
  canManageUser,
  getPermissionDescription,
  isSuperAdmin
} from '../utils/permissions';
import { cn } from '../utils/cn';
import { useUserStore } from '../stores/user-store';
import { TenantId, TENANT_CONFIG, getAllTenants } from '../config/tenant';
import { useI18n } from '../i18n';
import Loader from '../components/shared/Progressbar';

interface User {
  id: string;
  email: string;
  name: string | null;
  avatar_url: string | null;
  role: PermissionLevel;
  tenant: TenantId;
  can_access_all_tenants?: boolean;
  created_at: string;
  phone_number: string | null;
  language_preference: string;
  two_factor_enabled: boolean;
  last_login?: string;
}

const UserManagementPage: React.FC = () => {
  const { profile: currentUser } = useUserStore();
  const { t } = useI18n();
  const [users, setUsers] = useState<User[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<User[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<PermissionLevel | 'all'>('all');
  const [tenantFilter, setTenantFilter] = useState<TenantId | 'all'>('all');
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingRole, setEditingRole] = useState<PermissionLevel>('basic');
  const [editingTenant, setEditingTenant] = useState<TenantId>('cn');
  const [editingAllTenants, setEditingAllTenants] = useState(false);
  const [menuOpenUserId, setMenuOpenUserId] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Toast 状态
  const [toast, setToast] = useState<{ show: boolean; type: 'success' | 'error'; message: string }>({ show: false, type: 'success', message: '' });
  
  const showToast = (type: 'success' | 'error', message: string) => {
    setToast({ show: true, type, message });
    setTimeout(() => setToast({ show: false, type: 'success', message: '' }), 4000);
  };
  
  // 新用户创建表单状态
  const [newUserForm, setNewUserForm] = useState({
    email: '',
    name: '',
    role: 'basic' as PermissionLevel,
    sendInvite: true
  });

  const permissionLevels: PermissionLevel[] = ['basic', 'intermediate', 'advanced', 'uploader', 'admin'];

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    filterUsers();
  }, [users, searchQuery, roleFilter, tenantFilter]);

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      console.log('正在获取用户列表...');
      
      const { data, error } = await supabase
        .from('v_fs_users')
        .select('id, email, name, avatar_url, role, tenant, can_access_all_tenants, created_at, phone_number, language_preference, two_factor_enabled')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Supabase查询错误:', error);
        throw error;
      }
      
      console.log('获取到的用户数据:', data);
      setUsers(data || []);
    } catch (error) {
      console.error('获取用户列表失败:', error);
      alert('获取用户列表失败: ' + (error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  const filterUsers = () => {
    let filtered = users;

    // 搜索过滤
    if (searchQuery) {
      filtered = filtered.filter(user =>
        user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (user.name && user.name.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    // 角色过滤
    if (roleFilter !== 'all') {
      filtered = filtered.filter(user => user.role === roleFilter);
    }

    // 租户过滤（仅超级管理员可见多租户）
    // 修复：筛选某个租户时，也要包含 can_access_all_tenants 的用户
    if (tenantFilter !== 'all') {
      filtered = filtered.filter(user => 
        user.tenant === tenantFilter || user.can_access_all_tenants === true
      );
    }

    setFilteredUsers(filtered);
  };

  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setEditingRole(user.role);
    setEditingTenant(user.tenant);
    setEditingAllTenants(user.can_access_all_tenants || false);
    setShowEditModal(true);
    setMenuOpenUserId(null);
  };

  const handleDeleteUser = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
    setMenuOpenUserId(null);
  };

  const handleUpdateRole = async () => {
    if (!selectedUser || !currentUser) return;

    // 检查权限
    if (!canManageUser(currentUser.role, selectedUser.role, currentUser.can_access_all_tenants, selectedUser.can_access_all_tenants)) {
      showToast('error', '您没有权限管理此用户');
      return;
    }

    try {
      setIsSaving(true);
      const isSuperAdminUser = isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants);
      const tenantChanged = isSuperAdminUser && editingTenant !== selectedUser.tenant;
      const allTenantsChanged = isSuperAdminUser && editingAllTenants !== (selectedUser.can_access_all_tenants || false);
      
      console.log('正在更新用户:', {
        userId: selectedUser.id,
        fromRole: selectedUser.role,
        toRole: editingRole,
        fromTenant: selectedUser.tenant,
        toTenant: tenantChanged ? editingTenant : '(未变化)',
        allTenants: editingAllTenants,
        currentUserRole: currentUser.role
      });

      // 更新 users 表的 role
      const { data: userData, error: userError } = await supabase
        .from('users')
        .update({ role: editingRole })
        .eq('id', selectedUser.id)
        .select('id, role');

      if (userError) {
        console.error('Supabase更新users表错误:', userError);
        throw userError;
      }

      // 如果是超级管理员且租户设置有变化，更新 fs_user_profiles 表
      if (tenantChanged || allTenantsChanged) {
        const updateData: { tenant?: TenantId; can_access_all_tenants?: boolean } = {};
        if (tenantChanged) updateData.tenant = editingTenant;
        if (allTenantsChanged) updateData.can_access_all_tenants = editingAllTenants;
        
        const { error: profileError } = await supabase
          .from('fs_user_profiles')
          .update(updateData)
          .eq('id', selectedUser.id);

        if (profileError) {
          console.error('Supabase更新fs_user_profiles表错误:', profileError);
          throw profileError;
        }
      }

      console.log('数据库更新结果:', userData);

      if (!userData || userData.length === 0) {
        throw new Error('更新操作没有影响任何记录，可能是权限不足');
      }

      // 更新本地状态
      setUsers(users.map(user => 
        user.id === selectedUser.id 
          ? { 
              ...user, 
              role: editingRole, 
              tenant: tenantChanged ? editingTenant : user.tenant,
              can_access_all_tenants: allTenantsChanged ? editingAllTenants : user.can_access_all_tenants
            }
          : user
      ));

      setShowEditModal(false);
      setSelectedUser(null);
      
      // 构建成功消息
      let successParts: string[] = [];
      successParts.push(`角色更新为 ${PERMISSION_LABELS[editingRole]}`);
      if (tenantChanged) {
        successParts.push(`租户更改为 ${t(`tenant.${editingTenant}`)}`);
      }
      if (allTenantsChanged) {
        successParts.push(editingAllTenants ? '已开启多租户访问权限' : '已关闭多租户访问权限');
      }
      
      showToast('success', `用户更新成功：${successParts.join('，')}`);
    } catch (error: any) {
      console.error('更新用户角色失败:', error);
      
      let errorMessage = '更新失败：';
      if (error.message?.includes('permission')) {
        errorMessage += '数据库权限不足，请检查RLS策略';
      } else if (error.message?.includes('policy')) {
        errorMessage += '违反了数据库安全策略';
      } else if (error.code) {
        errorMessage += `数据库错误 (${error.code}): ${error.message}`;
      } else {
        errorMessage += error.message || '未知错误';
      }
      
      showToast('error', errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteUserConfirm = async () => {
    if (!selectedUser || !currentUser) return;

    // 检查权限
    if (!canManageUser(currentUser.role, selectedUser.role, currentUser.can_access_all_tenants, selectedUser.can_access_all_tenants)) {
      showToast('error', '您没有权限移除此用户');
      return;
    }

    // 防止移除自己
    if (selectedUser.id === currentUser.id) {
      alert('不能移除自己的账户');
      return;
    }

    try {
      // 多租户架构：只从文件系统移除用户，不删除整个账户
      // 1. 从 fs_user_profiles 删除
      const { error: profileError } = await supabase
        .from('fs_user_profiles')
        .delete()
        .eq('id', selectedUser.id);

      if (profileError) throw profileError;

      // 2. 从 user_app_registrations 删除文件系统注册记录
      await supabase
        .from('user_app_registrations')
        .delete()
        .eq('user_id', selectedUser.id)
        .eq('app', 'fs');

      // 更新本地状态
      setUsers(users.filter(user => user.id !== selectedUser.id));
      setShowDeleteModal(false);
      setSelectedUser(null);
      
      alert('用户已从文件系统移除。该用户仍可登录其他应用，如需重新访问文件系统需要管理员重新添加。');
    } catch (error) {
      console.error('移除用户失败:', error);
      alert('移除失败，请重试');
    }
  };

  const handleCreateUser = async () => {
    if (!currentUser || (currentUser.role !== 'admin' && currentUser.role !== 'super_admin')) {
      alert('只有管理员可以创建用户');
      return;
    }

    if (!newUserForm.email || !newUserForm.email.includes('@')) {
      alert('请输入有效的邮箱地址');
      return;
    }

    try {
      setIsCreating(true);

      // 调用Edge Function创建用户
      const { data, error } = await supabase.functions.invoke('create-user', {
        body: {
          email: newUserForm.email,
          name: newUserForm.name,
          role: newUserForm.role,
          sendInvite: newUserForm.sendInvite
        }
      });

      if (error) {
        console.error('创建用户失败:', error);
        throw new Error(error.message || '创建用户失败');
      }

      if (!data.success) {
        throw new Error(data.error || '创建用户失败');
      }

      // 刷新用户列表
      await fetchUsers();

      // 重置表单并关闭模态框
      setNewUserForm({
        email: '',
        name: '',
        role: 'basic',
        sendInvite: true
      });
      setShowCreateModal(false);

      // 显示成功消息
      let successMessage = '用户创建成功！';
      if (newUserForm.sendInvite) {
        if (data.invitation?.sent) {
          successMessage += '邀请邮件已发送。';
        } else if (data.invitation?.error) {
          successMessage += `但邀请邮件发送失败: ${data.invitation.error}`;
        }
      }

      alert(successMessage);

    } catch (error) {
      console.error('创建用户失败:', error);
      alert('创建用户失败: ' + (error as Error).message);
    } finally {
      setIsCreating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('zh-CN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getUserStats = () => {
    const stats = {
      total: users.length,
      basic: users.filter(u => u.role === 'basic').length,
      intermediate: users.filter(u => u.role === 'intermediate').length,
      advanced: users.filter(u => u.role === 'advanced').length,
      uploader: users.filter(u => u.role === 'uploader').length,
      admin: users.filter(u => u.role === 'admin').length,
    };
    return stats;
  };

  const stats = getUserStats();

  // 用户信息加载中，显示加载动画
  if (!currentUser) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  // 权限不足，显示拒绝访问
  if (currentUser.role !== 'admin' && currentUser.role !== 'super_admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <Shield className="mx-auto h-12 w-12 text-red-500 mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">访问被拒绝</h2>
          <p className="text-gray-600">您需要管理员权限才能访问用户管理页面</p>
          <p className="text-sm text-gray-500 mt-2">
            当前权限：{PERMISSION_LABELS[currentUser.role]}
          </p>
          {currentUser.role === 'uploader' && (
            <p className="text-sm text-blue-600 mt-2 bg-blue-50 px-4 py-2 rounded-lg inline-block">
              上传管理员可以管理文件和分类，但需要管理员权限才能管理用户
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Users className="w-8 h-8 mr-3 text-teal-600" />
            用户权限管理
          </h1>
          <p className="text-gray-600 mt-1">管理系统用户和权限设置</p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white bg-teal-600 rounded-lg hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500"
        >
          <UserPlus className="w-4 h-4 mr-2" />
          添加用户
        </button>
      </div>

      {/* 租户切换器 - 仅超级管理员可见 */}
      {currentUser && isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants) && (
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-4 rounded-xl border border-purple-200 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                <Globe className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className="text-sm font-medium text-purple-900">租户管理</p>
                <p className="text-xs text-purple-600">作为超级管理员，您可以管理所有租户的用户</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setTenantFilter('all')}
                className={cn(
                  'px-4 py-2 text-sm font-medium rounded-lg transition-all',
                  tenantFilter === 'all'
                    ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                    : 'bg-white text-purple-700 hover:bg-purple-100 border border-purple-200'
                )}
              >
                {t('tenant.allTenants')}
              </button>
              {getAllTenants().map((tenantId) => (
                <button
                  key={tenantId}
                  onClick={() => setTenantFilter(tenantId)}
                  className={cn(
                    'px-4 py-2 text-sm font-medium rounded-lg transition-all flex items-center gap-2',
                    tenantFilter === tenantId
                      ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-md'
                      : 'bg-white text-purple-700 hover:bg-purple-100 border border-purple-200'
                  )}
                >
                  <span>{TENANT_CONFIG[tenantId].flag}</span>
                  <span>{t(`tenant.${tenantId}`)}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* 统计卡片 */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
          <div className="flex items-center">
            <div className="p-2 bg-gray-100 rounded-full mr-3">
              <Users className="w-5 h-5 text-gray-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">总用户数</p>
              <p className="text-xl font-semibold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>

        {permissionLevels.map((level) => (
          <div key={level} className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
            <div className="flex items-center">
              <div className={cn(
                "p-2 rounded-full mr-3",
                PERMISSION_COLORS[level]
              )}>
                <Shield className="w-5 h-5" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{PERMISSION_LABELS[level]}</p>
                <p className="text-xl font-semibold text-gray-900">
                  {stats[level as keyof typeof stats]}
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* 搜索和过滤 */}
      <div className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="搜索用户邮箱或姓名..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          </div>
          
          <div className="sm:w-48">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value as PermissionLevel | 'all')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
            >
              <option value="all">所有角色</option>
              {permissionLevels.map((level) => (
                <option key={level} value={level}>
                  {PERMISSION_LABELS[level]}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* 用户列表 */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
        {isLoading ? (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="text-center py-12">
            <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">没有找到用户</h3>
            <p className="text-gray-500">
              {searchQuery || roleFilter !== 'all' 
                ? '尝试调整搜索条件或过滤器' 
                : '系统中暂无用户'}
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    用户
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    角色
                  </th>
                  {/* 租户列 - 仅超级管理员可见 */}
                  {currentUser && isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants) && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      租户
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    注册时间
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    状态
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    操作
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.avatar_url ? (
                            <img
                              className="h-10 w-10 rounded-full object-cover"
                              src={user.avatar_url}
                              alt={user.name || user.email}
                            />
                          ) : (
                            <div className="h-10 w-10 rounded-full bg-teal-100 flex items-center justify-center">
                              <span className="text-teal-600 font-medium text-sm">
                                {(user.name || user.email).charAt(0).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {user.name || '未设置姓名'}
                          </div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={cn(
                        "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium",
                        PERMISSION_COLORS[user.role]
                      )}>
                        <Shield className="w-3 h-3 mr-1" />
                        {PERMISSION_LABELS[user.role]}
                      </span>
                    </td>
                    {/* 租户单元格 - 仅超级管理员可见 */}
                    {currentUser && isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants) && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.can_access_all_tenants ? (
                          // 多租户访问权限 - 优化显示
                          <span className="inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium bg-gradient-to-r from-purple-50 to-pink-50 text-purple-700 border border-purple-200">
                            <span className="flex items-center gap-0.5">
                              <span>{TENANT_CONFIG['cn'].flag}</span>
                              <span className="text-purple-400">+</span>
                              <span>{TENANT_CONFIG['intl'].flag}</span>
                            </span>
                            <span className="ml-1.5">全区域</span>
                          </span>
                        ) : (
                          // 单租户
                          <span className={cn(
                            "inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium",
                            user.tenant === 'cn' 
                              ? 'bg-red-50 text-red-700 border border-red-200' 
                              : 'bg-blue-50 text-blue-700 border border-blue-200'
                          )}>
                            <span className="mr-1.5">{TENANT_CONFIG[user.tenant]?.flag || '🌐'}</span>
                            {t(`tenant.${user.tenant}`)}
                          </span>
                        )}
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          {user.two_factor_enabled ? (
                            <div className="flex items-center text-green-600">
                              <Shield className="w-4 h-4 mr-1" />
                              <span className="text-xs">2FA</span>
                            </div>
                          ) : (
                            <div className="text-gray-400">
                              <span className="text-xs">普通</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="relative">
                        <button
                          onClick={() => setMenuOpenUserId(
                            menuOpenUserId === user.id ? null : user.id
                          )}
                          className="text-gray-400 hover:text-gray-600 p-1"
                          disabled={user.id === currentUser.id}
                        >
                          <MoreVertical className="w-4 h-4" />
                        </button>
                        
                        {menuOpenUserId === user.id && (
                          <div className="absolute right-0 top-8 z-10 bg-white border border-gray-200 rounded-md shadow-lg w-44 py-1">
                            <button
                              onClick={() => handleEditUser(user)}
                              className="flex items-center w-full px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              disabled={!canManageUser(currentUser.role, user.role, currentUser.can_access_all_tenants, user.can_access_all_tenants)}
                            >
                              <Edit className="w-4 h-4 mr-2" />
                              编辑权限
                            </button>
                            <button
                              onClick={() => handleDeleteUser(user)}
                              className="flex items-center w-full px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                              disabled={!canManageUser(currentUser.role, user.role, currentUser.can_access_all_tenants, user.can_access_all_tenants) || user.id === currentUser.id}
                            >
                              <Trash2 className="w-4 h-4 mr-2" />
                              移除用户
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 编辑用户模态框 */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <Edit className="w-6 h-6 text-teal-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">编辑用户权限</h3>
              </div>
              
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">
                  用户: <span className="font-medium">{selectedUser.email}</span>
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  当前角色: <span className={cn(
                    "px-2 py-1 rounded text-xs font-medium",
                    PERMISSION_COLORS[selectedUser.role]
                  )}>
                    {PERMISSION_LABELS[selectedUser.role]}
                  </span>
                </p>
              </div>

              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  选择新角色
                </label>
                <div className="space-y-2">
                  {permissionLevels.map((level) => (
                    <button
                      key={level}
                      onClick={() => setEditingRole(level)}
                      className={cn(
                        "w-full p-3 border rounded-lg text-left transition-colors",
                        editingRole === level
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-gray-300"
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn(
                          "px-2 py-1 rounded text-xs font-medium",
                          PERMISSION_COLORS[level]
                        )}>
                          {PERMISSION_LABELS[level]}
                        </span>
                        {editingRole === level && (
                          <Check className="w-4 h-4 text-teal-600" />
                        )}
                      </div>
                      <p className="text-xs text-gray-600">
                        {getPermissionDescription(level)}
                      </p>
                    </button>
                  ))}
                </div>
              </div>

              {/* 租户选择 - 仅超级管理员可见 */}
              {currentUser && isSuperAdmin(currentUser.role, currentUser.can_access_all_tenants) && (
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    选择主租户区域
                  </label>
                  <div className="grid grid-cols-2 gap-3">
                    {/* 国内选项 */}
                    <button
                      onClick={() => setEditingTenant('cn')}
                      className={cn(
                        "p-4 border rounded-xl text-center transition-all",
                        editingTenant === 'cn'
                          ? "border-red-400 bg-red-50 ring-2 ring-red-200"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      )}
                    >
                      <span className="text-2xl block mb-1">🇨🇳</span>
                      <span className={cn(
                        "text-sm font-medium",
                        editingTenant === 'cn' ? 'text-red-700' : 'text-gray-700'
                      )}>
                        {t('tenant.cn')}
                      </span>
                      {editingTenant === 'cn' && (
                        <Check className="w-4 h-4 text-red-500 mx-auto mt-1" />
                      )}
                    </button>
                    
                    {/* 国际选项 */}
                    <button
                      onClick={() => setEditingTenant('intl')}
                      className={cn(
                        "p-4 border rounded-xl text-center transition-all",
                        editingTenant === 'intl'
                          ? "border-blue-400 bg-blue-50 ring-2 ring-blue-200"
                          : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
                      )}
                    >
                      <span className="text-2xl block mb-1">🌍</span>
                      <span className={cn(
                        "text-sm font-medium",
                        editingTenant === 'intl' ? 'text-blue-700' : 'text-gray-700'
                      )}>
                        {t('tenant.intl')}
                      </span>
                      {editingTenant === 'intl' && (
                        <Check className="w-4 h-4 text-blue-500 mx-auto mt-1" />
                      )}
                    </button>
                  </div>
                  
                  {/* 多租户访问权限开关 */}
                  <div className="mt-4 p-4 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-xl">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg">
                          <Globe className="w-4 h-4 text-white" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-purple-900">多租户访问权限</p>
                          <p className="text-xs text-purple-600">允许同时访问国内和国际两个区域</p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => setEditingAllTenants(!editingAllTenants)}
                        className={cn(
                          "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2",
                          editingAllTenants ? 'bg-purple-600' : 'bg-gray-200'
                        )}
                      >
                        <span
                          className={cn(
                            "pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out",
                            editingAllTenants ? 'translate-x-5' : 'translate-x-0'
                          )}
                        />
                      </button>
                    </div>
                    {editingAllTenants && (
                      <div className="mt-3 text-xs text-purple-700 bg-purple-100 p-2 rounded-lg">
                        ✓ 开启后，该用户可以在门户页面切换并访问所有租户的文件资源
                      </div>
                    )}
                  </div>

                  {!editingAllTenants && editingTenant !== selectedUser.tenant && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <p className="text-xs text-amber-700">
                          更改租户区域后，该用户将只能访问新区域的文件和资源。请确认此操作。
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowEditModal(false);
                    setSelectedUser(null);
                  }}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleUpdateRole}
                  disabled={isSaving}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center"
                >
                  {isSaving ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      保存中...
                    </>
                  ) : (
                    '保存更改'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 移除用户确认模态框 */}
      {showDeleteModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-10 h-10 mx-auto bg-amber-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-amber-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-medium text-gray-900">移除用户</h3>
                </div>
              </div>
              
              <div className="mb-6">
                <p className="text-sm text-gray-600">
                  您确定要将用户 <span className="font-medium">{selectedUser.email}</span> 从文件管理系统移除吗？
                </p>
                <div className="mt-3 p-3 bg-blue-50 rounded-lg">
                  <p className="text-sm text-blue-800">
                    <strong>说明：</strong>移除后该用户将无法访问文件管理系统，但其账户仍然存在，可以使用其他应用。如需恢复访问，管理员可重新添加。
                  </p>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  取消
                </button>
                <button
                  onClick={handleDeleteUserConfirm}
                  className="px-4 py-2 text-sm font-medium text-white bg-amber-600 border border-transparent rounded-md hover:bg-amber-700"
                >
                  确认移除
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 创建用户模态框 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <UserPlus className="w-6 h-6 text-teal-600 mr-3" />
                <h3 className="text-lg font-medium text-gray-900">添加新用户</h3>
              </div>
              
              <div className="space-y-4 mb-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    邮箱地址 *
                  </label>
                  <input
                    type="email"
                    value={newUserForm.email}
                    onChange={(e) => setNewUserForm({ ...newUserForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="user@example.com"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    用户姓名
                  </label>
                  <input
                    type="text"
                    value={newUserForm.name}
                    onChange={(e) => setNewUserForm({ ...newUserForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                    placeholder="可选"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    权限级别
                  </label>
                  <div className="space-y-2">
                    {permissionLevels.map((level) => (
                      <button
                        key={level}
                        onClick={() => setNewUserForm({ ...newUserForm, role: level })}
                        className={cn(
                          "w-full p-3 border rounded-lg text-left transition-colors",
                          newUserForm.role === level
                            ? "border-teal-500 bg-teal-50"
                            : "border-gray-200 hover:border-gray-300"
                        )}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className={cn(
                            "px-2 py-1 rounded text-xs font-medium",
                            PERMISSION_COLORS[level]
                          )}>
                            {PERMISSION_LABELS[level]}
                          </span>
                          {newUserForm.role === level && (
                            <Check className="w-4 h-4 text-teal-600" />
                          )}
                        </div>
                        <p className="text-xs text-gray-600">
                          {getPermissionDescription(level)}
                        </p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="flex items-center">
                  <input
                    id="sendInvite"
                    type="checkbox"
                    checked={newUserForm.sendInvite}
                    onChange={(e) => setNewUserForm({ ...newUserForm, sendInvite: e.target.checked })}
                    className="h-4 w-4 text-teal-600 focus:ring-teal-500 border-gray-300 rounded"
                  />
                  <label htmlFor="sendInvite" className="ml-2 block text-sm text-gray-700">
                    发送邀请邮件
                  </label>
                </div>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewUserForm({
                      email: '',
                      name: '',
                      role: 'basic',
                      sendInvite: true
                    });
                  }}
                  disabled={isCreating}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                >
                  取消
                </button>
                <button
                  onClick={handleCreateUser}
                  disabled={isCreating || !newUserForm.email}
                  className="px-4 py-2 text-sm font-medium text-white bg-teal-600 border border-transparent rounded-md hover:bg-teal-700 disabled:opacity-50 flex items-center"
                >
                  {isCreating ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      创建中...
                    </>
                  ) : (
                    <>
                      <UserPlus className="w-4 h-4 mr-2" />
                      创建用户
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Toast 提示 */}
      {toast.show && (
        <div className="fixed bottom-6 right-6 z-50 max-w-md animate-in slide-in-from-bottom-5 fade-in duration-300">
          <div className={cn(
            "flex items-start gap-3 p-4 rounded-xl shadow-lg border",
            toast.type === 'success'
              ? "bg-green-50 border-green-200 text-green-800"
              : "bg-red-50 border-red-200 text-red-800"
          )}>
            <div className={cn(
              "flex-shrink-0 p-1 rounded-full",
              toast.type === 'success' ? "bg-green-100" : "bg-red-100"
            )}>
              {toast.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium">
                {toast.type === 'success' ? '操作成功' : '操作失败'}
              </p>
              <p className="text-sm mt-0.5 opacity-90">{toast.message}</p>
            </div>
            <button
              onClick={() => setToast({ show: false, type: 'success', message: '' })}
              className={cn(
                "flex-shrink-0 p-1 rounded-full hover:bg-opacity-50 transition-colors",
                toast.type === 'success' ? "hover:bg-green-200" : "hover:bg-red-200"
              )}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPage;
